import React, { useState } from "react";
import {
  Grid,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Button,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  calculateAndFormatNoAbs,
  hashGetBlacklistCritera,
} from "../../../../Methods";
import EditRuleDialog from "./EditRuleDialog";
import { useDispatch } from "react-redux";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../../../../Redux/Reducers/uiSlice";
import { sendWebSocketMessage } from "../../../../../hooks/useWebSocket";

const RulesForm = ({ blacklistData, strategies, openBlacklistPopup, setBlacklistOpenPopup }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editedRule, setEditedRule] = useState(null);
  const [title, setTitle] = useState("Add Rule");
  const dispatch = useDispatch();

  const handleMenuClick = (event, rule) => {
    setAnchorEl(event.currentTarget);
    setSelectedRule(rule);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRule(null);
  };

  const handleEdit = () => {
    setTitle("Edit Rule");
    setEditedRule(selectedRule);
    setOpenEditDialog(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    const ruleObj = {
      type: selectedRule.type,
    };

    const data = {
      action: "modify_blacklist",
      data: {
        rules: {
          add: [],
          modify: [],
          remove: [],
        },
      },
    };

    const tickers = selectedRule.tickers || [];
    if (tickers.length == 0) {
      data.data.rules.remove.push({
        ...(selectedRule.strategy && { strategy: selectedRule.strategy }),
        ...ruleObj,
      });
    }
    tickers.forEach((ticker) => {
      data.data.rules.remove.push({
        ...(selectedRule.strategy && { strategy: selectedRule.strategy }),
        ticker: ticker,
        ...ruleObj,
      });
    });

    data.data.managerId = blacklistData.managerId;

    sendWebSocketMessage(JSON.stringify(data));
    dispatch(addActiveRequests("modify_blacklist"));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Updating Blacklist"));
    handleMenuClose();
    setBlacklistOpenPopup(false);
  };

  const handleAdd = () => {
    setEditedRule(null);
    setTitle("Add Rule");
    setOpenEditDialog(true);
  };

  const handleDialogClose = () => {
    setOpenEditDialog(false);
    setBlacklistOpenPopup(false);
    setEditedRule(null);
  };

  const handleSaveEdit = (updatedRule) => {
    // Handle save logic here
    setOpenEditDialog(false);
    setBlacklistOpenPopup(false);
  };

  const getOperator = (type, maximum, minimum) => {
    if (maximum && minimum && maximum === minimum) {
      return "=";
    }
    if (maximum) {
      return ">";
    }
    if (minimum) {
      return "<";
    }
    return "";
  };

  return (
    <>
      <Typography
        sx={{
          fontWeight: "600",
          fontSize: "20px",
          color: "#000",
          mb: 2,
        }}
      >
        {blacklistData?.rules?.length > 0 ? "Blacklist Rules" : "No Rules Available"}
      </Typography>
      <Box
        sx={{
          maxHeight: "320px",
          overflowY: "auto",
          width: "100%",
        }}
      >
        <Grid container spacing={1}>
          {blacklistData?.rules?.map((rule, ruleIndex) => {
            const {
              type,
              maximum,
              minimum,
              strategy,
              tickers,
              sectors,
              assetTypes,
            } = rule;
            const Strategy = strategy ? strategy.name : "All";
            const Ticker = tickers
              ? tickers.map((ticker) => ticker).join(", ")
              : "All";
            const operator = getOperator(type, maximum, minimum);

            let ruleText = hashGetBlacklistCritera(type);

            let msg = "";
            if (ruleText == "NotAllowed") {
              msg = "These tickers are not allowed to be traded";
            }
            if (ruleText == "Sectors") {
              const nonEmptySectors = sectors
                ?.filter((sector) => sector?.trim() !== "")
                ?.join(", ");

              if (nonEmptySectors) {
                const isSingular =
                  sectors?.filter((sector) => sector?.trim() !== "")?.length ===
                  1;
                const isSector =
                  sectors?.filter((sector) => sector?.trim() !== "")?.length ===
                  1;
                msg = `Stocks in  ${
                  isSector ? "sector" : "sectors"
                } : ${nonEmptySectors} ${
                  isSingular ? "is" : "are"
                } not allowed to be traded`;
              } else {
                msg = "";
              }
            }
            if (ruleText == "Assets") {
              const nonEmptyAssets = assetTypes
                ?.filter((asset) => asset?.trim() !== "")
                ?.join(", ");

              if (nonEmptyAssets) {
                const isSingular =
                  assetTypes?.filter((asset) => asset?.trim() !== "")
                    ?.length === 1;
                const isAsset =
                  assetTypes?.filter((asset) => asset?.trim() !== "")
                    ?.length === 1;
                msg = `Stocks in  ${
                  isAsset ? "asset" : "assets"
                } : ${nonEmptyAssets} ${
                  isSingular ? "is" : "are"
                } not allowed to be traded`;
              } else {
                msg = "";
              }
            }

            if (
              ruleText == "Price" ||
              ruleText == "Allocation" ||
              ruleText == "MarketCap" ||
              ruleText == "Shares" ||
              ruleText == "Volume"
            ) {
              if (operator == ">") {
                msg =
                  "Stocks with " +
                  ruleText.toLowerCase() +
                  " greater than " +
                  (["Price", "MarketCap", "Allocation", "Volume"].includes(ruleText)
                    ? "$" + calculateAndFormatNoAbs(maximum)
                    : maximum) +
                  " are not allowed";
              }
              if (operator == "<") {
                msg =
                  "Stocks with " +
                  ruleText.toLowerCase() +
                  " less than " +
                  (["Price", "MarketCap", "Allocation", "Volume"].includes(ruleText)
                    ? "$" + calculateAndFormatNoAbs(minimum)
                    : minimum) +
                  " are not allowed";
              }
              if (operator == "=") {
                msg =
                  "Stocks with " +
                  ruleText.toLowerCase() +
                  " equal to " +
                  (["Price", "MarketCap", "Allocation", "Volume"].includes(ruleText)
                    ? "$" + calculateAndFormatNoAbs(maximum)
                    : maximum) +
                  " are not allowed";
              }
            }

            return (
              <Grid
                container
                alignItems="center"
                key={ruleIndex}
                sx={{
                  padding: 2,
                  borderRadius: "8px",
                  marginBottom: 2,
                  backgroundColor: "#eee",
                  marginBottom: "3px",
                }}
              >
                <Grid
                  size={{ xs: 12 }}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography
                    sx={{
                      fontWeight: "500",
                      fontSize: "18px !important",
                      color: "#000",
                    }}
                  >
                    {msg}
                  </Typography>
                  <IconButton onClick={(event) => handleMenuClick(event, rule)}>
                    <MoreVertIcon />
                  </IconButton>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography
                    sx={{
                      color: "#333",
                      fontSize: "15px !important",
                    }}
                  >
                    Strategy:{" "}
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        fontSize: "15px !important",
                      }}
                    >
                      {Strategy}
                    </span>
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography
                    sx={{
                      color: "#333",
                      fontSize: "15px !important",
                    }}
                  >
                    Ticker:{" "}
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        fontSize: "15px !important",
                      }}
                    >
                      {Ticker}
                    </span>
                  </Typography>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <Button
        variant="contained"
        color="primary"
        onClick={handleAdd}
        sx={{ marginTop: "20px", float: "right" }}
      >
        Add Rule
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            width: "150px",
          },
        }}
      >
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      <EditRuleDialog
        open={openEditDialog}
        blacklistData={blacklistData}
        openBlacklistPopup={openBlacklistPopup}
        setBlacklistOpenPopup={setBlacklistOpenPopup}
        strategies={strategies}
        editedRule={editedRule}
        onClose={handleDialogClose}
        onSave={handleSaveEdit}
        title={title}
      />
    </>
  );
};

export default RulesForm;
